import { ReactNode, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTopLayer } from '@/hooks/useTopLayer'

interface FloatingPortalProps {
	children: ReactNode
}

/**
 * Renders floating UI (tooltips, popovers) into `document.body` so it escapes
 * ancestors with `overflow: hidden/auto/scroll`.
 *
 * The wrapper carries `.spaces-app` because the scoped resets and design tokens
 * in main.css are selected via `:is(#spaces-app, .spaces-app)` — without it the
 * portalled subtree renders unstyled. It has no dimensions of its own; the
 * floating child is `position: fixed`, so nothing is added to the body flow.
 *
 * It is also promoted to the top layer so floating UI opened from inside a
 * modal (itself top-layer) still paints above it.
 */
export function FloatingPortal ({ children }: FloatingPortalProps) {
	const layerRef = useRef<HTMLDivElement>(null)

	useTopLayer(layerRef)

	if (typeof document === 'undefined') return null

	return createPortal(
		<div ref={layerRef} className='spaces-app spaces-floating-layer'>
			{children}
		</div>,
		document.body
	)
}
